import { observer } from "mobx-react-lite";
import contentManager from "../../../stores/content-manager";
import bodyStylesManager from "../../../stores/body-styles-manager";
import SatoriCollapsiblePanel from "../shared/satori-ui/collapsible-panel";
import SatoriDimensionsBox from "../shared/satori-ui/dimensions-box";
import { IImageBlockData } from "../../../types/content-block-data";
import { Link, Unlink, Info, RulerDimensionLine, SquareSquare, ImagePlus } from "lucide-react";
import { cleanDimenValues } from "../../../lib/page-builder-lib";
import blocksManager from "../../../stores/blocks-manager";
import SatoriButton from "../shared/satori-ui/button";
import SatoriDropDownList from "../shared/satori-ui/dropdownlist";
import SatoriMultiLineBox from "../shared/satori-ui/multilinebox";
import { ALIGN_OPTIONS, IMAGE_POSITIONS, OBJECT_FIT } from "../../../types/constants";
// You can add a modal/dialog import here for the image picker

const DEFAULT_PLACEHOLDER = "https://placehold.co/600x400";

/**
 * ImageOptions – Sidebar Editing Panel for Image Blocks
 *
 * Provides a sidebar panel for configuring all properties of an Image content block in the Satori PageBuilder.
 * Includes image selection, alt text, description, caption, sizing, margins, border radius, alignment, object-fit,
 * and position controls, with support for linked/individual side editing for radius and margins.
 *
 * @component
 * @category OptionsPanel
 *
 * @param {Object} props - Component props.
 * @param {string} props.blockId - The unique ID of the Image content block to edit.
 *
 * @example
 * // Render image options panel for a given Image block
 * <ImageOptions blockId={blockId} />
 *
 * @remarks
 * - Fetches and updates the target Image block's data from the MobX contentManager store.
 * - Renders grouped UI controls for image selection, alt/description/caption fields, dimensions, border radius, and layout.
 * - All field changes call `contentManager.setImageConfig` for atomic and reactive MobX state updates.
 * - Supports both linked and individual side editing for border radius and margin, with px/rem unit conversion.
 * - Image selection uses a modal/gallery dialog (triggered via blocksManager).
 * - Fully reactive via MobX's `observer()` wrapper.
 * - Mirrors the editing panel structure of ButtonOptions, ColumnsOptions, and TextStyles for consistent UX.
 *
 * @see contentManager.setImageConfig
 * @see blocksManager.setOpenGalleryDialog – For opening the image picker
 * @see ButtonOptions, ColumnsOptions, TextStyles – For related sidebar editing panels
 */
export default observer(function ImageOptions({ blockId }: { blockId: string }) {
  const block = contentManager.getContentBlockById(blockId);
  if (!block || block.blockType !== "image") return null;
  const imageData = block.blockData as IImageBlockData;

	const parentFontSize = bodyStylesManager.getBodyStylesState().typography!["p"].fontSize;
  let parentRawFontSize: number = 0;  // We will always convert this into pixels first (if necessary)

  if (parentFontSize?.unit === "rem" || parentFontSize?.unit === "em")
    parentRawFontSize = parseFloat(parentFontSize!.value) * 16;
  else
    parentRawFontSize = parseInt(parentFontSize!.value);

  function update(field: string, value: any) {
    contentManager.setImageConfig(blockId, {
      ...imageData,
      [field]: value,
    });
  }

  return (
    <section className="flex flex-col">
      {/* 1. Image Picker Sub-Section */}
      <div className="flex items-center p-4 gap-2 justify-between">
        <SatoriButton
          variant={"light"}
          onClick={() => blocksManager.setOpenGalleryDialog(true)}
        >
          Pick an Image
        </SatoriButton>
        <img
          src={imageData.src || DEFAULT_PLACEHOLDER}
          alt="Selected"
          className="w-28 h-21 rounded border object-cover"
        />
      </div>

      {/* 2. Alt, Description, Caption */}
      <SatoriCollapsiblePanel
        icon={<Info size={18} strokeWidth={1.5} />}
        title={"Image Details"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          <SatoriMultiLineBox
            label={"Alt Text"}
            value={imageData.alt || ""}
            onChange={(val) => update("alt", val)}
            spellCheck={true}
            rows={3}
            placeholder="Enter an alternate text."
          />

          <SatoriMultiLineBox
            label={"Description"}
            value={imageData.description || ""}
            onChange={(val) => update("description", val)}
            spellCheck={true}
            rows={3}
            placeholder="Enter a description"
          />

          <SatoriMultiLineBox
            label={"Caption"}
            value={imageData.caption || ""}
            onChange={(val) => update("caption", val)}
            spellCheck={true}
            rows={3}
            placeholder="Enter a caption"
          />
        </div>
      </SatoriCollapsiblePanel>

      {/* 3. Styling: Border, Margin, Radius, Alignment, Fit, Width/Height */}
      {/* (You can re-use your DimensionsBox, Alignment dropdown, etc.) */}
      <SatoriCollapsiblePanel
        icon={<RulerDimensionLine size={18} strokeWidth={1.5} />}
        title={"Image Dimensions"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Width */}
          <label className="text-xs text-gray-500">Width</label>
          <SatoriDimensionsBox
            value={imageData.width?.value || ""}
            unit={imageData.width?.unit || "px"}
            onValueChange={(val: string) =>
              update("width", { ...imageData.width, value: val })
            }
            onUnitChange={(unitVal: string) =>
              update("width", { ...imageData.width, unit: unitVal })
            }
          />

          {/* Height */}
          <label className="text-xs text-gray-500">Height</label>
          <SatoriDimensionsBox
            value={imageData.height?.value || ""}
            unit={imageData.height?.unit || "px"}
            onValueChange={(val: string) =>
              update("height", { ...imageData.height, value: val })
            }
            onUnitChange={(unitVal: string) =>
              update("height", { ...imageData.height, unit: unitVal })
            }
          />
          {/* Border Radius */}
          <div className="flex items-center gap-2 my-2 w-full justify-between">
            <span className="text-sm">Border Radius</span>
            <button
              onClick={() =>
                update("borderRadius", {
                  ...imageData?.borderRadius,
                  linked: !imageData?.borderRadius?.linked,
                })
              }
              className="ml-1 p-1"
              title={
                imageData?.borderRadius?.linked
                  ? "Unlink sides"
                  : "Link all sides"
              }
            >
              {imageData?.borderRadius?.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>
          {imageData?.borderRadius?.linked ? (
            <div className="flex flex-col gap-2">
              <SatoriDimensionsBox
                value={
                  cleanDimenValues(imageData?.borderRadius?.topLeft!) || ""
                }
                unit={imageData?.borderRadius?.unit || "px"}
                onValueChange={(val: string) =>
                  update("borderRadius", {
                    ...imageData?.borderRadius!,
                    topLeft: val,
                    topRight: val,
                    bottomRight: val,
                    bottomLeft: val,
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedValue = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    update("borderRadius", {
                      topLeft: convertedValue,
                      topRight: convertedValue,
                      bottomRight: convertedValue,
                      bottomLeft: convertedValue,
                      unit: unitVal,
                      linked: imageData?.borderRadius?.linked ?? false,
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedValue = (
                      parseFloat(imageData?.borderRadius?.topLeft!) /
                      parentRawFontSize
                    )
                      .toFixed(3)
                      .toString();
                    update("borderRadius", {
                      topLeft: convertedValue,
                      topRight: convertedValue,
                      bottomRight: convertedValue,
                      bottomLeft: convertedValue,
                      unit: unitVal,
                      linked: imageData?.borderRadius?.linked ?? false,
                    });
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <SatoriDimensionsBox
                label={"Top Left"}
                value={
                  cleanDimenValues(imageData?.borderRadius?.topLeft!) || ""
                }
                unit={imageData?.borderRadius?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    borderRadius: {
                      ...imageData?.borderRadius!,
                      topLeft: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Top Right"}
                value={
                  cleanDimenValues(imageData?.borderRadius?.topRight!) || ""
                }
                unit={imageData?.borderRadius?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    borderRadius: {
                      ...imageData?.borderRadius!,
                      topRight: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Bottom Right"}
                value={
                  cleanDimenValues(imageData?.borderRadius?.bottomRight!) || ""
                }
                unit={imageData?.borderRadius?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    borderRadius: {
                      ...imageData?.borderRadius!,
                      bottomRight: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Bottom Left"}
                value={
                  cleanDimenValues(imageData?.borderRadius?.bottomLeft!) || ""
                }
                unit={imageData?.borderRadius?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    borderRadius: {
                      ...imageData?.borderRadius!,
                      bottomLeft: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) *
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) *
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) *
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTopLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.topLeft!) /
                        parentRawFontSize
                    ).toString();
                    const convertedTopRight = Math.round(
                      parseFloat(imageData?.borderRadius?.topRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomRight = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomRight!) /
                        parentRawFontSize
                    ).toString();
                    const convertedBottomLeft = Math.round(
                      parseFloat(imageData?.borderRadius?.bottomLeft!) /
                        parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      borderRadius: {
                        topLeft: convertedTopLeft,
                        topRight: convertedTopRight,
                        bottomRight: convertedBottomRight,
                        bottomLeft: convertedBottomLeft,
                        unit: unitVal,
                        linked: imageData?.borderRadius?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          )}

          <div className="flex items-center gap-2 my-2 w-full justify-between">
            <span className="text-sm">Image Margins</span>
            <button
              onClick={() =>
                contentManager.setImageConfig(blockId, {
                  margin: {
                    ...imageData?.margin!,
                    linked: !imageData?.margin?.linked,
                  },
                })
              }
              className="ml-1 p-1"
              title={
                imageData?.margin?.linked ? "Unlink sides" : "Link all sides"
              }
            >
              {imageData?.margin?.linked ? (
                <Link size={16} />
              ) : (
                <Unlink size={16} />
              )}
            </button>
          </div>
          {imageData?.margin?.linked ? (
            <div className="flex flex-col gap-2">
              <SatoriDimensionsBox
                value={cleanDimenValues(imageData?.margin?.left!) || ""}
                unit={imageData?.margin?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    margin: {
                      ...imageData?.margin!,
                      top: val,
                      right: val,
                      bottom: val,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedValue = Math.round(
                      parseFloat(imageData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedValue,
                        right: convertedValue,
                        bottom: convertedValue,
                        left: convertedValue,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedValue = (
                      parseFloat(imageData?.margin?.top!) / parentRawFontSize
                    )
                      .toFixed(3)
                      .toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedValue,
                        right: convertedValue,
                        bottom: convertedValue,
                        left: convertedValue,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <SatoriDimensionsBox
                label={"Top"}
                value={cleanDimenValues(imageData?.margin?.top!) || ""}
                unit={imageData?.margin?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    margin: {
                      ...imageData?.margin!,
                      top: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) * parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) / parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Right"}
                value={cleanDimenValues(imageData?.margin?.right!) || ""}
                unit={imageData?.margin?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    margin: {
                      ...imageData?.margin!,
                      right: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) * parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) / parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Bottom"}
                value={cleanDimenValues(imageData?.margin?.bottom!) || ""}
                unit={imageData?.margin?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    margin: {
                      ...imageData?.margin!,
                      bottom: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) * parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) / parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
              <SatoriDimensionsBox
                label={"Left"}
                value={cleanDimenValues(imageData?.margin?.left!) || ""}
                unit={imageData?.margin?.unit || "px"}
                onValueChange={(val: string) =>
                  contentManager.setImageConfig(blockId, {
                    margin: {
                      ...imageData?.margin!,
                      left: val,
                    },
                  })
                }
                onUnitChange={(unitVal: string) => {
                  if (unitVal === "px") {
                    // We need to change from rem/em value to pixel value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) * parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) * parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) * parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) * parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  } else if (unitVal === "rem" || unitVal === "em") {
                    // We need to change from pixel value to rem/em value
                    const convertedTop = Math.round(
                      parseFloat(imageData?.margin?.top!) / parentRawFontSize
                    ).toString();
                    const convertedRight = Math.round(
                      parseFloat(imageData?.margin?.right!) / parentRawFontSize
                    ).toString();
                    const convertedBottom = Math.round(
                      parseFloat(imageData?.margin?.bottom!) / parentRawFontSize
                    ).toString();
                    const convertedLeft = Math.round(
                      parseFloat(imageData?.margin?.left!) / parentRawFontSize
                    ).toString();
                    contentManager.setImageConfig(blockId, {
                      ...imageData,
                      margin: {
                        top: convertedTop,
                        right: convertedRight,
                        bottom: convertedBottom,
                        left: convertedLeft,
                        unit: unitVal,
                        linked: imageData?.margin?.linked ?? false,
                      },
                    });
                  }
                }}
              />
            </div>
          )}
        </div>
      </SatoriCollapsiblePanel>
      <SatoriCollapsiblePanel
        icon={<SquareSquare size={18} strokeWidth={1.5} />}
        title={"Image Layout"}
        defaultOpen={true}
      >
        <div className="flex flex-col gap-3">
          {/* Alignment */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Alignment"}
            options={ALIGN_OPTIONS}
            isFontFamily={false}
            value={imageData.alignment || "none"}
            onChange={(val) => update("alignment", val)}
          />

          {/* Image Fit (mapped to object-fit, or objectFit in React.CSSProperties) */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Image Fit"}
            options={OBJECT_FIT}
            isFontFamily={false}
            value={imageData.imageFit || "cover"}
            onChange={(val) => update("imageFit", val)}
          />

          {/* Image Position (mapped to object-position, or objectPosition in React.CSSProperties) */}
          <SatoriDropDownList
            layout="horizontal"
            label={"Image Position"}
            options={IMAGE_POSITIONS}
            isFontFamily={false}
            value={imageData.imagePosition || "center"}
            onChange={(val) => update("imagePosition", val)}
          />
        </div>
      </SatoriCollapsiblePanel>
    </section>
  );
});
